import {
  compact,
  DefaultMap,
  mapGroupBy,
} from '@seaber/common/lib/collections';
import {
  type Currency,
  type VoyageIncome_VoyageCharterFragment,
  PaymentUnit,
} from '../../../graphql';
import { type ScenarioOrder } from '../../../lib/planningData';

import { matchCoaAmountRange } from '../../contracts/coa_prices';
import { type LoadedAmount } from '../loadedAmounts';
import {
  convertToDefaultCurrency,
  getCurrencyRate,
  type GlobalStore,
} from '../../../state/zustand';
import { isDefined, memoize } from '../../../lib/util';
import { type IncomeParam } from './income';
import {
  calculateWorldscaleFreight,
  type WorldscaleFreightRateMap,
} from '../../../lib/worldscaleFreight';
import { calculateFreightAmount } from '../../../lib/freightCalculation';

export type OrderFreight = {
  orderId: number;
  price: number;
  unit: PaymentUnit;
  lumpSumUnit: string | null;
  currency: Currency | null;
  minAmount: number | null;
  maxAmount: number | null;
  coaAssortmentMatchedBy: 'ASSORTMENT' | 'ASSORTMENT_GROUP' | 'CATCHALL' | null;
};

export type FreightByOrderId = DefaultMap<number, OrderFreight[]>;

export function getFreightIncome(
  loadedAmount: LoadedAmount,
  freight: OrderFreight,
  currencyRates: GlobalStore['currencyRates'],
  worldscaleFreightRates?: WorldscaleFreightRateMap,
  order?: ScenarioOrder,
) {
  const { loadedMass, loadedVolume, loadedPcs } = loadedAmount;

  let incomeAmount: number | null = null;

  const params: IncomeParam[] = [
    {
      name: 'Freight rate',
      value: freight.price,
      testId: 'income-freight-rate',
    },
    {
      name: 'Freight currency',
      value: freight.currency,
      testId: 'income-freight-currency',
    },
    {
      name: 'Freight unit',
      value: freight.unit,
      testId: 'income-freight-unit',
    },
  ];

  const freightRate = freight.currency
    ? convertToDefaultCurrency(freight.price, freight.currency)
    : freight.price;

  if (freight.currency && currencyRates) {
    const currencyRate = getCurrencyRate(freight.currency);
    if (currencyRate) {
      params.push({
        name: 'Currency rate',
        value: currencyRate,
        testId: 'order-currency-rate',
      });
    }
  }
  {
    if (!isDefined(freightRate)) {
      console.error(
        'Currency conversion failed for freight',
        freight,
        currencyRates,
      );
    } else {
      incomeAmount = calculateFreightAmount({
        mass: loadedMass,
        volume: loadedVolume,
        pcs: loadedPcs,
        freightRate,
        freightUnit: freight.unit,
        worldscaleFreightRates,
        order,
      });

      // Add specific parameters based on unit type
      switch (freight.unit) {
        case PaymentUnit.PER_SET: {
          params.push({
            testId: 'order-planned-amount-set',
            name: 'Planned amount (set)',
            value: loadedPcs,
          });
          break;
        }
        case PaymentUnit.PER_PCS: {
          params.push({
            testId: 'order-planned-amount-pcs',
            name: 'Planned amount (pcs)',
            value: loadedPcs,
          });
          break;
        }
        case PaymentUnit.M3:
        case PaymentUnit.SCBM_UB:
        case PaymentUnit.FT3: {
          params.push({
            testId: 'order-planned-volume-cbm',
            name: 'Planned volume (cbm)',
            value: loadedVolume,
          });
          break;
        }
        case PaymentUnit.MT:
        case PaymentUnit.BDMT: {
          params.push({
            testId: 'order-planned-mass-mt',
            name: 'Planned mass (mt)',
            value: loadedMass,
          });
          break;
        }
        case PaymentUnit.WORLDSCALE: {
          // For worldscale, we need to get the flatrate for display
          const worldscaleResult = calculateWorldscaleFreight(
            freight.price,
            worldscaleFreightRates,
            order,
            loadedMass,
          );
          if (worldscaleResult !== null) {
            params.push({
              testId: 'worldscale-flatrate',
              name: 'Worldscale flatrate',
              value: worldscaleResult.flatRate,
            });
          }
          break;
        }
      }
    }

    return { incomeAmount, params };
  }
}

export function getFreightByLoadedAmount(
  freightByOrderId: FreightByOrderId,
  loadedAmount: LoadedAmount,
) {
  const freightsByAmount = freightByOrderId
    .get(loadedAmount.orderId)
    .filter((freight) => {
      const amountMatches = matchCoaAmountRange(
        {
          mass: loadedAmount.loadedMass,
          volume: loadedAmount.loadedVolume,
        },
        {
          cargoUnit: freight.lumpSumUnit,
          maxAmount: freight.maxAmount,
          minAmount: freight.minAmount,
          paymentUnit: freight.unit,
        },
      );

      return amountMatches;
    });

  return findBestMatchingOrderFreight(freightsByAmount);
}

export function findBestMatchingOrderFreight(freights: OrderFreight[]) {
  let bestMatch: OrderFreight | null = null;

  for (const freight of freights) {
    if (freight.coaAssortmentMatchedBy === null) {
      bestMatch = freight;
      break;
    }

    if (!bestMatch) {
      bestMatch = freight;
      continue;
    }

    if (freight.coaAssortmentMatchedBy === 'ASSORTMENT') {
      bestMatch = freight;
      continue;
    }

    if (
      freight.coaAssortmentMatchedBy === 'ASSORTMENT_GROUP' &&
      bestMatch.coaAssortmentMatchedBy !== 'ASSORTMENT'
    ) {
      bestMatch = freight;
      continue;
    }

    if (
      freight.coaAssortmentMatchedBy === 'CATCHALL' &&
      bestMatch.coaAssortmentMatchedBy !== 'ASSORTMENT_GROUP' &&
      bestMatch.coaAssortmentMatchedBy !== 'ASSORTMENT'
    ) {
      bestMatch = freight;
    }
  }

  freights.forEach((freight) => {
    if (!bestMatch) {
      bestMatch = freight;
      return;
    }
  });

  return bestMatch;
}

export function getFreightsByOrderId(
  /** Can be either incoming or outgoing voyage charters */
  voyageChartersById: Map<number, VoyageIncome_VoyageCharterFragment>,
  orderById: Map<number, ScenarioOrder>,
  /** Set true to generate 0 freight for orders with no freight */
  opts?: {
    addMissingFreights?: boolean;
  },
): FreightByOrderId {
  const freightByOrderId = new DefaultMap<number, OrderFreight[]>(() => []);
  const voyageCharterByOrderId = new Map<
    number,
    VoyageIncome_VoyageCharterFragment
  >();

  for (const o of orderById.values()) {
    const orderId = o.id;

    const outgoing = o.outgoingVoyageCharterRelation;
    const outgoingVcId = outgoing?.voyageCharterId;
    const outgoingVc =
      outgoingVcId != null && voyageChartersById.get(outgoingVcId);
    if (outgoingVc) {
      voyageCharterByOrderId.set(orderId, outgoingVc);
    }

    const incoming = o.incomingVoyageCharterRelation;
    const incomingVcId = incoming?.voyageCharterId;
    const incomingVc =
      incomingVcId != null && voyageChartersById.get(incomingVcId);
    if (incomingVc) {
      voyageCharterByOrderId.set(orderId, incomingVc);
    }
  }

  /** Optimization: in some contexts the number of orders is small relative to all voyage charters, iterate only voyage charters referred to by the orders. The function would work correctly without this too. */
  const voyageCharterIdsInOrders = new Set(
    [...orderById.values()].flatMap((o) =>
      compact([
        o.outgoingVoyageCharterRelation?.voyageCharterId,
        o.incomingVoyageCharterRelation?.voyageCharterId,
      ]),
    ),
  );

  voyageCharterIdsInOrders.forEach((voyageCharterId) => {
    const voyageCharter = voyageChartersById.get(voyageCharterId);
    if (!voyageCharter) return;
    const orderFreights = getVoyageCharterFreights(voyageCharter, orderById);

    orderFreights.forEach((freight) => {
      freightByOrderId.get(freight.orderId).push(freight);
    });
  });

  /* Add 0 freight for orders without freight */
  if (opts?.addMissingFreights) {
    orderById.forEach((order) => {
      if (!freightByOrderId.has(order.id)) {
        const voyageCharter = voyageCharterByOrderId.get(order.id);
        const currency = voyageCharter?.currency ?? null;

        freightByOrderId.set(order.id, [
          {
            orderId: order.id,
            unit: PaymentUnit.MT,
            price: 0,
            currency,
            maxAmount: null,
            minAmount: null,
            lumpSumUnit: null,
            coaAssortmentMatchedBy: null,
          },
        ]);
      }
    });
  }

  return freightByOrderId;
}

const getVcOrderRelsByVcId = memoize(
  (orderById: Map<number, ScenarioOrder>) => {
    const allRels = compact([
      ...orderById
        .values()
        .flatMap((o) => [
          o.incomingVoyageCharterRelation,
          o.outgoingVoyageCharterRelation,
        ]),
    ]) satisfies NonNullable<unknown>[];

    const byVcId = mapGroupBy(allRels, (rel) => rel.voyageCharterId);

    return byVcId;
  },
);

const getVcOrderRels = (
  voyageCharter: VoyageIncome_VoyageCharterFragment,
  orderById: Map<number, ScenarioOrder>,
): NonNullable<ScenarioOrder['incomingVoyageCharterRelation']>[] => {
  const relsByVcId = getVcOrderRelsByVcId(orderById);

  const orderFreights = relsByVcId.get(voyageCharter.id);

  if (!orderFreights) {
    return [];
  }

  return orderFreights;
};

/**
 * Returns all freight rates that could be applied to related orders, exact
 * match for COA rates cannot be given as amount information might not be
 * available at this point
 */
export function getVoyageCharterFreights(
  voyageCharter: VoyageIncome_VoyageCharterFragment,
  orderById: Map<number, ScenarioOrder>,
): OrderFreight[] {
  const orderFreights: OrderFreight[] = [];

  const voyageCharterOrderRels: (
    | NonNullable<ScenarioOrder['incomingVoyageCharterRelation']>
    | NonNullable<ScenarioOrder['outgoingVoyageCharterRelation']>
  )[] = getVcOrderRels(voyageCharter, orderById);

  const coa =
    voyageCharter.voyageCharterToContractsByVoyageCharterId.nodes[0]
      ?.coaContractByCoaId;

  voyageCharterOrderRels.forEach((vcRel) => {
    const freight =
      vcRel?.voyageCharterOrderFreightsByOrderVoyageCharterRelationId
        .nodes[0] ?? null;

    if (!freight?.freightAmount || !freight.freightUnit) {
      return;
    }

    orderFreights.push({
      orderId: vcRel.orderId,
      price: Number(freight.freightAmount),
      unit: freight.freightUnit,
      lumpSumUnit: null,
      currency: voyageCharter.currency ?? null,
      minAmount: null,
      maxAmount: null,
      coaAssortmentMatchedBy: null,
    });
  });

  if (coa) {
    const coaAssortments =
      coa.coaContractAssortmentsByCoaContractId.nodes.filter(isDefined);
    const coaPrices =
      coa.coaContractPricesByCoaContractId.nodes.filter(isDefined);
    const coaAssortmentById = new Map(coaAssortments.map((a) => [a.id, a]));

    voyageCharterOrderRels.forEach((rel) => {
      const orderId = rel.orderId;
      const order = orderById.get(orderId);

      if (!order) {
        return;
      }
      const routeId = rel.coaRouteId;

      coaPrices.forEach((price) => {
        const coaAssortment = coaAssortmentById.get(price.coaAssortmentId);
        if (!coaAssortment) {
          return;
        }

        const route = price.coaContractPortByRouteId;
        const isCatchAll =
          coaAssortment.assortmentId === null &&
          coaAssortment.assortmentGroupId === null;

        const groupMatches =
          !!coaAssortment.assortmentGroupId &&
          coaAssortment.assortmentGroupId ===
            order.assortmentByAssortmentId?.assortmentGroup;

        const assortmentMatches =
          !!coaAssortment.assortmentId &&
          coaAssortment.assortmentId === order.assortmentId;

        const someAssortmentMatches =
          isCatchAll || groupMatches || assortmentMatches;

        // TODO: zone matching seems to be missing here
        const routeMatches = routeId
          ? route?.id === routeId
          : route?.originId === order.loadPortId &&
            route?.destinationId === order.dischargePortId;

        if (!someAssortmentMatches || !routeMatches || !price.price) {
          return;
        }

        const { paymentUnit, minAmount, maxAmount } = coaAssortment;

        orderFreights.push({
          orderId,
          price: Number(price.price),
          unit: paymentUnit,
          lumpSumUnit: coa.cargoUnitByAmountUnitId?.name ?? null,
          currency: coa.currency ?? null,
          minAmount: minAmount ? +minAmount : null,
          maxAmount: maxAmount ? +maxAmount : null,
          coaAssortmentMatchedBy: assortmentMatches
            ? 'ASSORTMENT'
            : groupMatches
              ? 'ASSORTMENT_GROUP'
              : 'CATCHALL',
        });
      });
    });
  }

  return orderFreights;
}
